import NiceModal from '@ebay/nice-modal-react';

import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';
import Icon from 'components/global/Icon';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';

import ImportOrderVerifyModal from 'pages/ImportOrders/ImportOrderVerifyModal';
import useCreateShipment from 'pages/Shipments/ActionForms/CreateShipment/Components/useCreateShipment';

import AppLogger from 'utils/AppLogger';

import { faCircleInfo, faCopy, faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';

import * as P from '../PriceCalculator.styled';
import * as S from './LogisticDetailsForImports.styled';

const LogisticDetailsForImports = ({ nextStep, prevStep }) => {
	const { showInfo } = useAlert();
	const {
		getValues,
		handleSubmit,
		formState: { isLoading, isSubmitting },
		reset,
		setValue
	} = useFormContext();
	const { generalSettings, clientCards } = useSelector((state) => state?.user);
	const isNotCRDocVerified = generalSettings?.CRDocStatus !== 'VERIFIED';
	const doesNotHaveClientCard = clientCards ? clientCards.length === 0 : true;
	const priceConfig = getValues('priceConfig');
	const addressData = priceConfig?.nearestBranch;
	const shippingCompany = priceConfig?.nameAr;
	const createShipment = useCreateShipment({ type: 'import' });
	const { t } = useTranslation();

	const loading = isSubmitting || isLoading;

	const addressItems = [
		{
			label: 'streetAddress',
			value: addressData?.address ?? '-'
		},
		{
			label: 'stateProvince',
			value: addressData?.state ?? '-'
		},
		{
			label: 'city',
			value: addressData?.city ?? '-'
		},
		{
			label: 'zipOrPostalCode',
			value: addressData?.zipCode ?? '-'
		}
	];

	const CopyAddressButton = ({ classNameSuffix }) => (
		<Button
			className={`copy-button-${classNameSuffix}`}
			icon={faCopy}
			variant="outline"
			label="copyFullAddress"
			onClick={() => {
				navigator.clipboard.writeText(`${addressItems.map((item) => item.value).join(', ')}`);
				showInfo({
					header: t('copiedToClipboard')
				});
			}}
		/>
	);

	const onSubmit = async () => {
		const boxes = getValues('boxes');
		const totalDue = getValues('totalDue');
		const trackingUrl = getValues('trackingURL');
		const trackingNumber = getValues('trackingNumber');
		const supplierURL = getValues('supplierURL');

		await createShipment(
			{
				items: [
					{
						orderID: getValues('orderID'),
						boxes,
						manualShipment: true,
						dcSettingsID: priceConfig.deliveryCompanySettingsId,
						packageWeight: Number(
							boxes.reduce((acc, curr) => Number(acc) + Number(curr.weight), 0).toFixed(1)
						),
						price: priceConfig.price,
						priceOptionID: priceConfig.otoDeliveryPriceId,
						pickingType: priceConfig.pickupType,
						totalDue
					}
				],
				externalTrackingUrl: trackingUrl,
				externalTrackingNumber: trackingNumber,
				supplierUrl: supplierURL
			},
			(shipment) => {
				setValue('shipmentResultData', shipment);
				nextStep();
			},
			() => {
				showInfo({
					header: 'error',
					message: 'error.createShipment'
				});
			}
		);
	};

	useEffect(
		() => () => {
			reset({
				...getValues(),
				trackingUrl: null,
				trackingNumber: null,
				supplierURL: null
			});
		},
		[]
	);

	return (
		<P.ShippingDocumentsAndPickupOptionsCard fullWidth rounded>
			<P.CardContent>
				<S.AddressFieldWrapper>
					<S.TitleWrapper>
						<S.Title>
							<Typography variant="p2" fontWeight="700">
								<Trans i18nKey="fullfillmentAddress" values={{ shippingcompany: t(shippingCompany) }} />
							</Typography>
							<Tooltip title="fullfillmentAddressTooltip">
								<Icon icon={faCircleInfo} />
							</Tooltip>
						</S.Title>
						<CopyAddressButton classNameSuffix="desktop" />
					</S.TitleWrapper>
					<S.AddressFieldValues>
						{addressItems.map((item) => (
							<S.AddressItems key={item.label}>
								<Typography variant="p3" color="var(--content-3)">
									{item.label}
								</Typography>
								<S.AddressValue>
									<Typography variant="p3" fontWeight="500">
										{item.value}
									</Typography>
									<Button
										variant="transparent"
										icon={faCopy}
										iconSize="small"
										size="x-small"
										onClick={() => {
											navigator.clipboard.writeText(item.value);
											showInfo({
												header: t('copiedToClipboard')
											});
										}}
									/>
								</S.AddressValue>
							</S.AddressItems>
						))}
					</S.AddressFieldValues>
					<CopyAddressButton classNameSuffix="mobile" />
				</S.AddressFieldWrapper>
				<P.Divider />
				<S.TrackingDetailsWrapper>
					<S.Title>
						<Typography variant="p2" fontWeight="700">
							trackingDetails
						</Typography>
						<Tooltip title="trackingDetailsTooltip">
							<Icon icon={faCircleInfo} />
						</Tooltip>
					</S.Title>
					<S.FormWrapper>
						<FormItem label="trackingURL" required>
							<StringInput name="trackingURL" />
						</FormItem>
						<FormItem label="supplierURL" required>
							<StringInput name="supplierURL" />
						</FormItem>
						<FormItem label="trackingNumber" required>
							<StringInput name="trackingNumber" />
						</FormItem>
					</S.FormWrapper>
				</S.TrackingDetailsWrapper>
				<P.Divider />
				<S.InstructionsWrapper>
					<S.Title>
						<Typography variant="p2" fontWeight="700">
							instructions
						</Typography>
					</S.Title>

					<S.Instructions>
						<Icon size="large" icon={faExclamationCircle} />
						<S.TextWrapper>
							<Typography variant="p3" fontWeight="500">
								<Trans
									i18nKey="instructionsImportOrders"
									components={{
										linkViewer: <LinkViewer />,
										span: <span />
									}}
								/>
							</Typography>
						</S.TextWrapper>
					</S.Instructions>
					<S.Instructions>
						<Icon size="large" icon={faExclamationCircle} />
						<S.TextWrapper className="listInstruction">
							<Typography variant="p3" fontWeight="500">
								<Trans
									i18nKey="instructionsLogisticDetails"
									components={{
										linkViewer: <LinkViewer />,
										ul: <ul />,
										li: <li />,
										span: <span />
									}}
								/>
							</Typography>
						</S.TextWrapper>
					</S.Instructions>
				</S.InstructionsWrapper>
			</P.CardContent>
			<P.ShippingDocumentsAndPickupOptionsButtonContainer>
				<Button
					icon={faChevronLeft}
					label="previous"
					variant="outline"
					onClick={() => {
						prevStep();
					}}
				/>
				<Button
					icon={faChevronRight}
					loading={loading}
					iconPos="right"
					label="createShipment"
					onClick={
						isNotCRDocVerified || doesNotHaveClientCard
							? () => NiceModal.show(ImportOrderVerifyModal)
							: handleSubmit(onSubmit, (errors) => {
									AppLogger.log(errors);
								})
					}
				/>
			</P.ShippingDocumentsAndPickupOptionsButtonContainer>
		</P.ShippingDocumentsAndPickupOptionsCard>
	);
};

export default LogisticDetailsForImports;

const LinkViewer = ({ children }) => {
	const { t } = useTranslation();
	return (
		<a target="_blank" href={children} rel="noreferrer">
			{t(children)}
		</a>
	);
};
